import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BeneficiaryCard from "./BeneficiaryCard";
import { isMobile } from "../../utils/miscutils";
import { useField, useFormikContext } from "formik";
import MobileSecurityFooter from "../../customFiles/MobileSecurityFooter";

export default function UserDetailsForm(props) {
  const [isMobileView, setIsMobileView] = useState(isMobile(window.innerWidth));
  const { setFieldValue } = useFormikContext();
  const [field] = useField("employeeDetails");
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile(window.innerWidth));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBeneficiarySelect = (beneficiary) => {
    setFieldValue("employeeDetails", beneficiary.name);
    sessionStorage.setItem("relationName", beneficiary.name);
    // Trigger categories recalculation in parent component
    if (props.setSelectedBeneficiary) {
      props.setSelectedBeneficiary(beneficiary.name);
    }
  };

  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const hasScrollLeft = scrollLeft > 0;
      const hasScrollRight = scrollLeft < scrollWidth - clientWidth - 10;

      if (canScrollLeft !== hasScrollLeft) {
        setCanScrollLeft(hasScrollLeft);
      }
      if (canScrollRight !== hasScrollRight) {
        setCanScrollRight(hasScrollRight);
      }
    }
  }, [canScrollLeft, canScrollRight]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    checkScrollButtons();

    const scrollHandler = () => {
      checkScrollButtons();
    };

    container.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", checkScrollButtons, { passive: true });

    return () => {
      container.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("resize", checkScrollButtons);
    };
  }, [props.users.benefs.length, checkScrollButtons]);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -350, behavior: "smooth" });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 350, behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <Box paddingBottom={4} paddingTop={1} marginTop={"50px"}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            display:{md:"none"},
            marginBottom: 2,
            color: "#252D38",
          }}
        >
          Select Beneficiary
        </Typography>

        {isMobileView ? (
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              overflowX: "auto",
              overflowY: "hidden",
              paddingBottom: "8px",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#BFC8D2",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#8A99AD",
                },
              },
            }}
          >
            {props.users.benefs.map((beneficiary, index) => (
              <BeneficiaryCard
                key={beneficiary.maid || index}
                beneficiary={beneficiary}
                selected={field.value === beneficiary.name}
                onSelect={handleBeneficiarySelect}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "23px",
                  color: "#252D38",
                }}
              >
                Select Beneficiary
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  onClick={scrollLeft}
                  disabled={!canScrollLeft}
                  sx={{
                    backgroundColor: "#F0F3F5",
                    width: 36,
                    height: 36,
                    borderRadius: "30px",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    "&:disabled": { backgroundColor: "#f9f9f9", opacity: 0.5 },
                  }}
                >
                  <ArrowBackIcon
                    sx={{
                      color: canScrollLeft ? "#2E3742" : "#BCC6D1",
                      fontSize: 22,
                    }}
                  />
                </IconButton>

                <IconButton
                  onClick={scrollRight}
                  disabled={!canScrollRight}
                  sx={{
                    backgroundColor: "#F0F3F5",
                    width: 36,
                    height: 36,
                    borderRadius: "30px",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    "&:disabled": { backgroundColor: "#f9f9f9", opacity: 0.5 },
                  }}
                >
                  <ArrowForwardIcon
                    sx={{
                      color: canScrollRight ? "#2E3742" : "#BCC6D1",
                      fontSize: 22,
                    }}
                  />
                </IconButton>
              </Box>
            </Box>

            <Box
              ref={scrollContainerRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: "16px",
                paddingBottom: 2,
                paddingTop: 1,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                msOverflowStyle: "none",
              }}
            >
              {props.users.benefs.map((beneficiary, index) => (
                <Box
                  key={beneficiary.maid || index}
                  sx={{
                    minWidth: "320px",
                    flexShrink: 0,
                  }}
                >
                  <BeneficiaryCard
                    beneficiary={beneficiary}
                    selected={field.value === beneficiary.name}
                    onSelect={handleBeneficiarySelect}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            marginTop: 2,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              color: "#1778F2",
              fontSize: "20px",
              marginRight: 1,
              marginTop: "2px",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#47566A",
              lineHeight: 1.5,
            }}
          >
            Ensure the prescription/invoice matches the beneficiary.
          </Typography>
        </Box>
      </Box>
    </>
  );
}